import {useState, useEffect} from 'react';
import {useNavigate, Navigate, useLocation, useSearchParams, Link} from 'react-router-dom';
import {TrendingUp, Eye, EyeOff, AlertCircle, Mail, Lock, ArrowRight, Sparkles} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Card} from '@/components/ui/card';
import {useAuth} from '@/contexts/AuthContext';
import {useBranding} from '@/contexts/BrandingContext';
import {toast} from 'sonner';
import {DEFAULT_PATH_FOR_ROLE} from '@/lib/roles';
import {canAccessRoute} from '@/lib/permissions';

// Plataforma (fixa, marca do serviço)
const PLATFORM_LOGO = '/branding/condutor-online-logo.png';
const PLATFORM_NAME = 'condutor.online';

export const LoginPage = () => {
    const {user, login, logout} = useAuth();
    const {branding} = useBranding();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const forceShow = searchParams.get('force') === '1';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Se "forçar login" via ?force=1 (ex: link do Mobile Preview), termina sessão atual
    // para mostrar sempre o formulário em vez de redirecionar.
    useEffect(() => {
        if (forceShow && user) {
            logout();
        }
    }, [forceShow, user, logout]);

    if (user && !forceShow) {
        // Convidado/password resetada → obrigar troca antes de entrar.
        if (user.mustChangePassword) return <Navigate to="/first-login" replace/>;
        const fallback = DEFAULT_PATH_FOR_ROLE[user.role] || '/';
        const requested = location.state?.from?.pathname;
        // Só seguir o "from" pedido se o role atual TIVER acesso a essa rota.
        // Caso contrário, cair para o landing default do role (evita loop de Acesso Negado).
        const from = requested && canAccessRoute(requested, user.permissions) ? requested : fallback;
        return <Navigate to={from} replace/>;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!email.trim() || !password) {
            setError('Preencha email e palavra-passe.');
            return;
        }
        setLoading(true);
        const r = await login({email: email.trim(), password});
        setLoading(false);
        if (!r.ok) {
            setError(r.error);
            return;
        }
        toast.success(`Bem-vindo, ${r.name || ''}`);
        if (r.mustChangePassword) {
            navigate('/first-login', {replace: true});
            return;
        }
        const fallback = DEFAULT_PATH_FOR_ROLE[r.role] || '/';
        const requested = location.state?.from?.pathname;
        const target = requested && canAccessRoute(requested, r.permissions) ? requested : fallback;
        navigate(target, {replace: true});
    };

    return (
        <div className="min-h-screen w-full bg-slate-950 flex items-stretch" data-testid="login-page">
            {/* Left: brand panel — azul royal + accent amarelo */}
            <div className="hidden lg:flex flex-1 relative overflow-hidden">
                <div className="absolute inset-0" style={{
                    background: 'radial-gradient(circle at 25% 35%, rgba(37,99,235,0.45), transparent 55%), radial-gradient(circle at 80% 75%, rgba(234,179,8,0.18), transparent 55%), linear-gradient(135deg, #0c1a3a 0%, #0a0f1f 100%)'
                }}/>
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
                        backgroundSize: '60px 60px',
                    }}
                />
                {/* Big yellow accent blob */}
                <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{backgroundColor: '#facc15'}}/>
                <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
                    <div className="flex items-center gap-5 flex-wrap">
                        {/* CLIENTE — protagonista (maior) */}
                        <div className="flex items-center gap-4">
                            {branding.logo && branding.logo !== PLATFORM_LOGO ? (
                                <img
                                    src={branding.logo}
                                    alt={branding.companyName || 'Cliente'}
                                    className="h-16 w-auto rounded-xl bg-white/10 backdrop-blur p-1.5 ring-1 ring-white/10"
                                    data-testid="client-logo"
                                />
                            ) : (
                                <div className="rounded-xl p-3" style={{backgroundColor: branding.accentColor || '#4f46e5'}}>
                                    <TrendingUp size={32} className="text-white"/>
                                </div>
                            )}
                            <div>
                                <h1 className="font-secondary font-bold text-2xl xl:text-3xl leading-tight" data-testid="client-name">
                                    {branding.companyName || PLATFORM_NAME}
                                </h1>
                                {branding.companySubtitle && (
                                    <p className="text-sm text-slate-300">{branding.companySubtitle}</p>
                                )}
                            </div>
                        </div>
                        {/* condutor.online — adicional, mais discreta */}
                        <div className="flex items-center gap-2 ml-auto">
                            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Powered by</span>
                            <div className="bg-black rounded-lg px-2.5 py-1.5 ring-1 ring-white/10">
                                <img src={PLATFORM_LOGO} alt={PLATFORM_NAME} className="h-7 w-auto block" data-testid="platform-logo"/>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6 max-w-lg">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                             style={{backgroundColor: '#facc15', color: '#0c1a3a'}}>
                            <Sparkles size={12}/>
                            Plataforma completa de gestão
                        </div>
                        <h2 className="font-secondary font-bold text-4xl xl:text-5xl leading-tight">
                            Tags inteligentes <br/>
                            para uma <span style={{color: '#facc15'}}>frota sem fronteiras</span>.
                        </h2>
                        <p className="text-slate-300 text-base leading-relaxed">
                            Notificações automáticas por SMS, email e chamada com IVR. Veja em tempo real o estado de cada veículo, equipamento ou ocorrência reportada por QR code.
                        </p>
                        <div className="grid grid-cols-3 gap-3 pt-2">
                            {[
                                {v: '24h', l: 'Cobertura'},
                                {v: 'IVR', l: 'Chamadas com teclado'},
                                {v: '3', l: 'Canais simultâneos'},
                            ].map(s => (
                                <div key={s.l} className="rounded-lg border border-yellow-400/30 bg-white/5 backdrop-blur p-3">
                                    <p className="font-secondary font-bold text-2xl" style={{color: '#facc15'}}>{s.v}</p>
                                    <p className="text-[11px] text-slate-300 mt-0.5">{s.l}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-500">
                        <p>© {new Date().getFullYear()} {branding.companyName || PLATFORM_NAME} — Todos os direitos reservados.</p>
                        <div className="flex items-center gap-1.5">
                            <span>Powered by</span>
                            <span className="font-bold text-slate-300">{PLATFORM_NAME}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right: login form */}
            <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-gradient-to-br from-slate-50 to-blue-50">
                <div className="w-full max-w-md">
                    <div className="lg:hidden flex items-center gap-3 mb-8">
                        {branding.logo && branding.logo !== PLATFORM_LOGO ? (
                            <img src={branding.logo} alt={branding.companyName || 'Cliente'} className="h-11 w-auto"/>
                        ) : (
                            <div className="rounded-xl p-2" style={{backgroundColor: branding.accentColor || '#4f46e5'}}>
                                <TrendingUp size={22} className="text-white"/>
                            </div>
                        )}
                        <h1 className="font-secondary font-bold text-lg text-slate-900 flex-1">{branding.companyName || PLATFORM_NAME}</h1>
                        <div className="bg-black rounded px-2 py-1 ring-1 ring-slate-200">
                            <img src={PLATFORM_LOGO} alt={PLATFORM_NAME} className="h-5 w-auto block"/>
                        </div>
                    </div>

                    <Card className="p-8 shadow-2xl border-blue-100 relative overflow-hidden">
                        {/* yellow accent bar at top */}
                        <div className="absolute top-0 left-0 right-0 h-1.5" style={{backgroundColor: '#facc15'}}/>
                        <div className="mb-6">
                            <h2 className="text-2xl font-secondary font-bold text-slate-900">Iniciar sessão</h2>
                            <p className="text-sm text-slate-500 mt-1">Entre na sua conta para gerir a frota</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                            <div className="space-y-2">
                                <Label className="text-sm" htmlFor="email">Email</Label>
                                <div className="relative">
                                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                    <Input id="email" type="email" autoComplete="email" placeholder="seuemail@empresa.pt"
                                           value={email} onChange={e => setEmail(e.target.value)} className="pl-9"
                                           data-testid="login-email"/>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm" htmlFor="password">Palavra-passe</Label>
                                    <Link
                                        to={email.trim() ? `/password/reset?email=${encodeURIComponent(email.trim())}` : '/password/reset'}
                                        className="text-xs text-blue-600 hover:underline"
                                    >
                                        Esqueci-me
                                    </Link>
                                </div>
                                <div className="relative">
                                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                    <Input id="password" type={showPwd ? 'text' : 'password'} autoComplete="current-password"
                                           value={password} onChange={e => setPassword(e.target.value)} className="pl-9 pr-10"
                                           data-testid="login-password"/>
                                    <button type="button" onClick={() => setShowPwd(s => !s)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                                        {showPwd ? <EyeOff size={15}/> : <Eye size={15}/>}
                                    </button>
                                </div>
                            </div>

                            {error && (
                                <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                                    <AlertCircle size={14} className="mt-0.5 flex-shrink-0"/>
                                    <span>{error}</span>
                                </div>
                            )}

                            <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 h-11 text-white"
                                    disabled={loading} data-testid="login-submit">
                                {loading ? 'A entrar...' : <>Entrar <ArrowRight size={16} className="ml-2"/></>}
                            </Button>
                        </form>
                    </Card>

                    <p className="text-center text-xs text-slate-500 mt-6">
                        Ainda não tem conta? Contacte o administrador da sua organização.
                    </p>
                    <div className="flex items-center justify-center gap-2 mt-3 text-[10px] text-slate-400">
                        <span>Powered by</span>
                        <div className="bg-black rounded px-1.5 py-1">
                            <img src={PLATFORM_LOGO} alt={PLATFORM_NAME} className="h-3 w-auto block"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
